import React, { useState, useEffect, useRef } from 'react';
import section6Video from './technical diagram.mp4';
import './Section6.css';

const Section6 = () => {
  const [textFadeProgress, setTextFadeProgress] = useState(0);
  const [videoFadeProgress, setVideoFadeProgress] = useState(1);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Text fades in as section enters (rect.top: viewportHeight -> 0)
      if (rect.top > 0) {
        const progress = 1 - (rect.top / viewportHeight);
        setTextFadeProgress(Math.max(0, Math.min(1, progress)));
        setVideoFadeProgress(1); // Video stays fully visible
      }
      // Video fades out as section exits (rect.top: 0 -> -viewportHeight)
      else if (rect.top <= 0 && rect.top > -viewportHeight) {
        setTextFadeProgress(1); // Text stays fully visible
        const progress = 1 + (rect.top / viewportHeight);
        setVideoFadeProgress(Math.max(0, Math.min(1, progress)));
      }
      // Section completely scrolled past
      else {
        setTextFadeProgress(1);
        setVideoFadeProgress(0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleVideoReady = () => {
    setIsVideoLoading(false);
  };

  // Start video autoplay when component mounts
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
      });
    }
  }, []);

  return (
    <section className="section6-container" ref={containerRef}>
      <div className="section6-content">

        {/* Left Side: Video */}
        <div className="section6-left">
          <video
            ref={videoRef}
            className="section6-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={handleVideoReady}
            onCanPlay={handleVideoReady}
            onError={handleVideoReady}
            src={section6Video}
            style={{
              opacity: videoFadeProgress
            }}
          />
          {isVideoLoading && (
            <div className="section6-video-loader" aria-live="polite">
              <div className="section6-video-spinner" />
              <span className="section6-video-loader-text">Loading demo...</span>
            </div>
          )}
        </div>

        {/* Right Side: Text Content */}
        <div className="section6-right">
          <div
            className="section6-text-wrapper"
            style={{
              opacity: textFadeProgress
            }}
          >
            <h2 className="section6-title">Product Philosophy</h2>
            <p className="section6-body">
              AtomicArch aims to transform these disconnected manual tasks into a seamless, high-velocity digital process. The core philosophy revolves around the concept of the "architectural digital asset" and "atomicity". Users simply chat with the platform, and the system dynamically executes "atomic design moves" to translate natural language into fully editable, engineering-grade 3D models, 2D drawings, and quantitative data.
            </p>
            <p className="section6-body">
              Because each request acts as a complete, system-wide operation, a single adjustment instantaneously updates the entire project context. This deterministic approach solves the current "Text-to-3D gap" — unlike other AI tools that only generate visual content, AtomicArch produces parametric, real-world engineering geometries.
            </p>
            <h3 className="section6-subtitle">The Technology Stack</h3>
            <p className="section6-body">
              The platform combines three core layers: a Proprietary CAD Engine (40%) for geometric generation, a Proprietary Architectural Semantic Engine (40%) for architectural logic and data structures, and a Large Language Model (20%) that converts natural language into precise engineering parameters for both engines.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Section6;
